import {
    ApolloError,
    useLazyQuery,
    useMutation,
    useQuery,
    useReactiveVar,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { CurrentUserActiveFirmQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import {
    AssociationModifierList,
    AssociationModifierListName,
} from '@deltek/specification-client/core/Models/associationModifierList'
import {
    CurrentUserActiveFirmResponse,
    CurrentUserFirmsProps,
} from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    FirmId,
    FirmResponse,
} from '@deltek/specification-client/core/Models/firm'
import {
    GetFilterInput,
    GetResultsInput,
    GetSortInput,
    ProjectItem,
} from '@deltek/specification-client/core/Models/projectItem'
import { ProjectParameter } from '@deltek/specification-client/core/Models/projectParameter'
import { ProjectPhase } from '@deltek/specification-client/core/Models/projectPhase'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import {
    getKpPageItemValues,
    updateKpMainLocalStore,
} from '@deltek/specification-client/core/Utilities/localstorageUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    SpecIdType,
    TableFetchNextDataProps,
} from '@deltek/specification-client/core/Widgets/SpecTable'
import SpecTableTitleSettings, {
    SpecTableTitleSettingsProps,
} from '@deltek/specification-client/core/Widgets/SpecTable/SpecTableTitleSettings'
import { PinningProps } from '@deltek/specification-client/core/Widgets/SpecTable/settings/TableSettings'
import { Theme, makeStyles } from '@material-ui/core/styles'
import {
    AllProjectsResponse,
    SetFavoriteProjectInput,
    TableProjectItem,
} from 'Models/project'
import { RemoveProjectMutation } from 'api/graphQL/mutations/project.mutations'
import { SetFavoriteProjectMutation } from 'api/graphQL/mutations/projectItem.mutations'
import { FirmProjectParametersQuery } from 'api/graphQL/queries/firm.queries'
import { GetProjectsQuery } from 'api/graphQL/queries/project.queries'
import { CurrentUserCanCreateProjectQuery } from 'api/graphQL/queries/user.queries'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { RemoveProjectInput } from 'models/projectInput'
import React, { useCallback, useEffect } from 'react'
import {
    getFacetDropDownOptions,
    getUpdatedSkipCountForProjects,
} from 'shared/utils/workspaceUtils'
import NewProjectSlideOut, {
    CREATE_PROJECT_DENIED_MESSAGE,
} from '../components/NewProjectSlideOut'
import ProjectGridView from './components/GridView/ProjectGridView'
import {
    ProjectFilterContext,
    ProjectFilterContextInterface,
} from './components/ProjectFilterContext'
import ProjectTitleBar from './components/ProjectTitleBar'
import ProjectsPageEmptyState from './components/ProjectsPageEmptyState'
import RecentProjectsWidget from './components/RecentProjectsWidget'
import RemoveProjectWarning from './components/RemoveProjectWarning'
import ProjectTableView from './components/TableView/ProjectTableView'

const useStyles = makeStyles((theme: Theme) => ({
    widgetContainer: {
        backgroundColor: '#f2f2f2',
        marginBottom: 18,
    },
    projectsHeaderContainer: {
        backgroundColor: theme.palette.secondary.main,
        display: 'flex',
        alignItems: 'center',
    },
    sectionHeader: {
        textTransform: 'uppercase',
        color: '#fff',
        padding: '6px 0px',
        fontSize: pxToRem(16),
    },
    projectsHomePageContainer: {
        marginTop: theme.spacing(2),
    },
    recentProjectsContainer: {
        height: 135,
        backgroundColor: 'lightGray',
    },

    headerButtons: {
        '& .MuiIconButton-root': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.secondary.main,
            padding: theme.spacing(1),
        },
    },
    gridViewSettingsBar: {
        ...theme.typography.h5,
        fontSize: pxToRem(14),
        '& .table-title-container': {
            color: '#04325B',
            textTransform: 'uppercase',
        },
        display: 'flex',
        flexWrap: 'wrap',
        backgroundColor: '#f2f2f2',
        margin: 0,
        '& .view-as-card.active,.view-filter.active': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    gridContainer: {
        display: 'block',
    },
    projectTitleBar: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.common.white,
        textTransform: 'uppercase',
    },
    projectViewContainer: {
        marginTop: 0,
        backgroundColor: '#F2F2F2',
    },
    tableViewContainer: {
        marginTop: 0,
        padding: '0px !important ',
        '& .expand-collapse-all-container': {
            display: 'none',
        },
        '& .view-as-option-divider': {
            display: 'none',
        },
        '& .load-next': {
            display: 'none',
        },
    },
}))
interface IdNameProps {
    id: string | undefined
    name: string
}
export const defaultPageSize: number = 25
const emptyFilterSelection: Partial<GetFilterInput>[] = []
const localStorageKey = 'ProjectsHomePageConfig'
const localStorageItemKey = (userId: string) => `projectFilter${userId}`
const initialSorting: Partial<GetSortInput>[] = [
    { sortField: 'groupName', sortOrder: 'asc' },
    { sortField: 'groupLastUpdated', sortOrder: 'desc' },
    { sortField: 'lastUpdated', sortOrder: 'desc' },
]
const pinnedColumns: PinningProps = {
    firstColumnPinned: true,
    lastColumnPinned: false,
}
const getLocalStorageValue = (
    field: string,
    currentUserId: string,
    emptyValue: string | string[]
) => {
    const sectionsModalLocalStore = getKpPageItemValues({
        currentUserId,
        pageKey: localStorageKey,
    })

    if (sectionsModalLocalStore === undefined) return emptyValue
    return sectionsModalLocalStore[field] ?? emptyValue
}
export interface GetAssociationModifierProps {
    parameters?: Partial<ProjectParameter>[]
    modifierListName: AssociationModifierListName
}

export const getAssociationModifier = ({
    parameters,
    modifierListName,
}: GetAssociationModifierProps) => {
    if (!parameters) return []
    const projectParam = parameters.find(
        (param) => param.modifierList?.name === modifierListName
    )

    return (
        projectParam?.selectedModifiers
            ?.filter((selectedModifier) => !!selectedModifier.name)
            .map((selectedModifier) => selectedModifier.name) ?? []
    )
}

const ProjectsHomePage: React.FC = () => {
    const classes = useStyles()
    const [slideOutOpen, toggleSlideOut] = React.useState<boolean>(false)
    const [fetchNextLoading, setFetchNextLoading] = React.useState<boolean>(
        false
    )
    const { currentUserInfo, isAdmin } = useReactiveVar(userInfo)

    const { data: currentUserActiveFirmResponse } = useQuery<
        CurrentUserActiveFirmResponse
    >(CurrentUserActiveFirmQuery)

    const activeFirmRole: string = React.useMemo(() => {
        return currentUserActiveFirmResponse?.myActiveFirm.firmRole ?? ''
    }, [currentUserActiveFirmResponse])

    const activeFirmInfo: CurrentUserFirmsProps = React.useMemo(() => {
        return (
            currentUserActiveFirmResponse?.myActiveFirm ??
            ({} as CurrentUserFirmsProps)
        )
    }, [currentUserActiveFirmResponse])

    const isFirmAdmin = activeFirmInfo?.firmRole === 'Admin'
    const isPrimaryAuthor =
        (activeFirmInfo?.firmRole === 'Author' ||
            activeFirmInfo?.firmRole === 'PublishingAuthor') &&
        !!activeFirmInfo?.isPrimary

    // The per-user permission only gates primary authors — firm admins always have it,
    // and every other role never sees the button, so skip the query for them.
    const {
        data: canCreateProjectData,
        refetch: refetchCanCreateProject,
    } = useQuery<{
        user: { id: string; canCreateProject?: boolean }
    }>(CurrentUserCanCreateProjectQuery, {
        skip: !isPrimaryAuthor,
        variables: {
            firmId: currentUserInfo.account.accountId ?? '',
            userId: currentUserInfo.kpUserId ?? '',
        },
    })

    const hasCreateProjectPermission =
        canCreateProjectData?.user?.canCreateProject ?? true

    const showAddProjectButton = isFirmAdmin || isPrimaryAuthor

    const canCreateProject =
        isFirmAdmin || (isPrimaryAuthor && hasCreateProjectPermission)

    const isReviewer = !!currentUserInfo?.roles?.[0]?.includes('Reviewer')

    // Authors keep the full layout even without permission so they see the inactive
    // button; only roles that can never create projects get the read-only message.
    const emptyStateVariant =
        (isFirmAdmin && !isReviewer) || isPrimaryAuthor ? 'full' : 'readOnly'

    const [projectFilterValues, setProjectFilterValues] = React.useState<
        Partial<GetFilterInput>[]
    >(emptyFilterSelection)
    const [projectSortValues, setProjectSortValues] = React.useState<
        Partial<GetSortInput>[]
    >(initialSorting)
    const [toggleCardView, setToggleCardView] = React.useState(true)
    const [isFilterExpanded, setFilterExpansion] = React.useState<boolean>(
        false
    )
    const [skipCount, setSkipCount] = React.useState<number>(0)
    const [projectsDisplayed, setProjectsDisplayed] = React.useState<number>(0)
    const [totalProjectsCount, setTotalProjectsCount] = React.useState<number>(
        0
    )
    const [expanded, setExpanded] = React.useState<
        Record<SpecIdType<TableProjectItem>, boolean>
    >({})
    const [buildingTypeDropDown, setBuildingTypeDropDown] = React.useState<
        string[] | undefined
    >(undefined)
    const [projectPhaseDropDown, setProjectPhaseDropDown] = React.useState<
        string[] | undefined
    >(undefined)
    const [lodDropDown, setLodDropDown] = React.useState<string[] | undefined>(
        undefined
    )
    const [budgetDropDown, setBudgetDropDown] = React.useState<
        string[] | undefined
    >(undefined)
    const [regionDropDown, setRegionDropDown] = React.useState<
        string[] | undefined
    >(undefined)
    const [countryDropDown, setCountryDropDown] = React.useState<
        string[] | undefined
    >(undefined)
    const userId = currentUserInfo?.kpUserId ?? ''
    const firmId = currentUserInfo?.account?.accountId ?? ''
    const [
        isRecentProjectsRefresh,
        setIsRecentProjectsRefresh,
    ] = React.useState<boolean>(false)
    const [projectIdToRemove, setProjectIdToRemove] = React.useState<string>('')
    const [isOpenConfirmation, setIsOpenConfirmation] = React.useState<boolean>(
        false
    )

    const { error: firmProjectParametersError } = useQuery<
        FirmResponse,
        FirmId
    >(FirmProjectParametersQuery, {
        fetchPolicy: 'no-cache',
        variables: {
            firmId: firmId ?? '',
        },
        onCompleted: (data) => {
            const projectParam = data?.firm?.associationModifierLists?.find(
                (param: Partial<AssociationModifierList>) =>
                    param?.name === 'Building Type'
            )
            const buildingTypeDropDownList = (
                projectParam?.associationModifiers || []
            ).map((item: Partial<IdNameProps>) => item.name ?? '')
            if (buildingTypeDropDownList && buildingTypeDropDownList.length) {
                setBuildingTypeDropDown(buildingTypeDropDownList)
            }
            const projectPhasesDropDownList = (
                data?.firm?.projectPhases || []
            ).map((phase: Partial<ProjectPhase>) => phase.name ?? '')
            if (projectPhasesDropDownList && projectPhasesDropDownList.length) {
                const completePhaseDropDownList = [
                    ...projectPhasesDropDownList,
                    'Not Set',
                ]
                completePhaseDropDownList.sort(function (a, b) {
                    if (a.toLowerCase() < b.toLowerCase()) {
                        return -1
                    }
                    if (a.toLowerCase() > b.toLowerCase()) {
                        return 1
                    }
                    return 0
                })
                setProjectPhaseDropDown(completePhaseDropDownList)
            }
            const lodDropDownList = (data?.firm?.projectPhases || []).map(
                (phase: Partial<ProjectPhase>) => {
                    if (phase?.loD) {
                        return phase.loD.toString()
                    }
                    return ''
                }
            )
            const uniqueLODs: string[] = Array.from(new Set(lodDropDownList))
            if (lodDropDownList && lodDropDownList.length) {
                setLodDropDown([...uniqueLODs, 'Not Set'])
            }
        },
    })
    const {
        updateCustomContent,
        setActionBarVisibility,
        updateBrowserTitle,
    } = React.useContext(AppBarContext)

    useEffect(() => {
        updateCustomContent?.(null)
        setActionBarVisibility(false)
        updateBrowserTitle?.('Projects')
    }, [updateCustomContent, setActionBarVisibility, updateBrowserTitle])

    useEffect(() => {
        if (userId) {
            const saved = getLocalStorageValue(
                localStorageItemKey(userId),
                userId,
                ''
            )
            const pastItems = saved !== '' ? JSON.parse(saved as string) : {}
            if (pastItems.filterValues) {
                setProjectFilterValues(pastItems.filterValues)
            }
            if (pastItems.filterView) {
                setFilterExpansion(pastItems.filterView)
            }
            if (pastItems.isCardView === false) {
                setToggleCardView(false)
            }
            if (pastItems.sortValues) {
                setProjectSortValues(pastItems.sortValues)
            }
        }
    }, [userId])

    const context: ProjectFilterContextInterface = {
        projectFilterValues,
        setProjectFilterValues,
    }

    const [isGridExpanded, setIsGridExpanded] = React.useState<
        Record<string, boolean>
    >({})

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const [
        getProjectFilters,
        {
            data: projectsData,
            loading: projectsLoading,
            error: projectsError,
            fetchMore: projectsDataFetchMore,
            networkStatus: projectDataStatus,
        },
    ] = useLazyQuery<AllProjectsResponse>(GetProjectsQuery, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        onCompleted: (data: AllProjectsResponse) => {
            let projectsCount = data?.getProjects.count || 0
            setTotalProjectsCount(projectsCount)
            const displayedProjectsCount =
                data?.getProjects.projects.length ?? 0
            setProjectsDisplayed(
                displayedProjectsCount <= projectsCount
                    ? displayedProjectsCount
                    : projectsCount
            )
            setBudgetDropDown(
                getFacetDropDownOptions(data?.getProjects.facets, 'Budget')
            )
            setRegionDropDown(
                getFacetDropDownOptions(data?.getProjects.facets, 'Region')
            )
            setCountryDropDown(
                getFacetDropDownOptions(data?.getProjects.facets, 'Country')
            )
        },
    })

    const triggerProjectFilter = useCallback(() => {
        setSkipCount(0)
        getProjectFilters({
            variables: {
                input: {
                    limit: defaultPageSize,
                    skip: 0,
                    sortOrders: [...projectSortValues],
                    filters: [...projectFilterValues],
                } as Partial<GetResultsInput>,
            },
        })
    }, [getProjectFilters, projectSortValues, projectFilterValues])

    React.useEffect(() => {
        triggerProjectFilter()
    }, [triggerProjectFilter])

    const fetchNextData = ({
        totalRows,
    }: Partial<TableFetchNextDataProps<TableProjectItem>>) => {
        setFetchNextLoading(true)
        let updatedSkipCount = getUpdatedSkipCountForProjects(
            skipCount,
            (projectsData?.getProjects?.count ?? 0) - skipCount
        )
        if (
            projectsData?.getProjects?.count &&
            totalRows &&
            totalRows < projectsData?.getProjects?.count
        ) {
            setSkipCount(updatedSkipCount)
            if (projectsDataFetchMore) {
                projectsDataFetchMore({
                    variables: {
                        input: {
                            skip: updatedSkipCount,
                            limit: defaultPageSize,
                            sortOrders: [...projectSortValues],
                            filters: [...projectFilterValues],
                        } as Partial<GetResultsInput>,
                    },
                    updateQuery: (prev, { fetchMoreResult }) => {
                        if (!fetchMoreResult) return prev

                        setProjectsDisplayed(
                            prev.getProjects.projects.length +
                                fetchMoreResult.getProjects.projects.length
                        )
                        const fetchMoreProjects =
                            fetchMoreResult.getProjects.projects
                        const lastProjectGroupName =
                            fetchMoreProjects?.[fetchMoreProjects.length - 1]
                                ?.groupName ?? ''
                        if (toggleCardView) {
                            setIsGridExpanded({
                                ...isGridExpanded,
                                [`groupName:${lastProjectGroupName}`]:
                                    Object.keys(isGridExpanded)[0].includes(
                                        lastProjectGroupName
                                    ) &&
                                    Object.keys(isGridExpanded).length === 1
                                        ? true
                                        : false,
                            })
                        } else {
                            setExpanded({
                                ...expanded,
                                [`groupName:${lastProjectGroupName}`]: false,
                            })
                        }
                        setFetchNextLoading(false)

                        return {
                            getProjects: {
                                ...prev.getProjects,
                                count: fetchMoreResult.getProjects.count,
                                projects: [
                                    ...prev.getProjects.projects!,
                                    ...fetchMoreResult.getProjects.projects!,
                                ],
                            } as Partial<GetResultsInput>,
                        } as AllProjectsResponse
                    },
                })
            }
        }
    }

    const [favoriteProject] = useMutation<
        { setFavoriteProject: Partial<ProjectItem> },
        { input: SetFavoriteProjectInput }
    >(SetFavoriteProjectMutation)

    // the click event must pass in favorite and projectId as argument.
    const handleFavoriteButtonClick = (
        isFavoriteState: boolean,
        projectId: string
    ) => {
        favoriteProject({
            variables: {
                input: {
                    isFavoriteState,
                    projectId,
                },
            },
        })
    }

    const handleFilterView = (showFilter: boolean) => {
        setFilterExpansion(showFilter)
        if (userId) {
            updateKpMainLocalStore({
                currentUserId: userId,
                pageKey: localStorageKey,
                itemKey: localStorageItemKey(userId),
                itemValue: JSON.stringify({
                    filterValues: projectFilterValues,
                    filterView: showFilter,
                    isCardView: toggleCardView,
                    sortValues: projectSortValues,
                }),
            })
        }
    }

    const handleExpandCollapse = (panel: string | undefined) => (
        event: React.ChangeEvent<{}>,
        isExpanded: boolean
    ) => {
        if (panel) {
            setIsGridExpanded({
                ...isGridExpanded,
                [`groupName:${panel}`]: isExpanded,
            })
        }
    }
    /**
     * Re-checks the create-project permission before opening the slide-out. The
     * button state is only as fresh as the last fetch, so an admin revoking access
     * mid-session would otherwise let the user fill in the whole form before the
     * save failed. Only primary authors are gated — admins always have permission.
     */
    const handleAddProjectButtonClick = async () => {
        if (isPrimaryAuthor) {
            try {
                const { data } = await refetchCanCreateProject()
                if (data?.user?.canCreateProject === false) {
                    showGlobalSnackbar({
                        variables: {
                            message: CREATE_PROJECT_DENIED_MESSAGE,
                            variant: 'error',
                            id: 'ProjectsHomePage_CreateProjectDenied',
                        },
                    })
                    return
                }
            } catch {
                // Let the attempt through on a failed check — the save is still
                // enforced server-side, so this only affects where the error surfaces.
            }
        }
        toggleSlideOut(true)
    }
    const handleCloseSlideOut = () => {
        toggleSlideOut(false)
    }

    const toggleRemoveWarning = () => {
        setIsOpenConfirmation(!isOpenConfirmation)
    }

    const openRemoveDialog = (projectId: string | undefined) => {
        if (projectId) {
            setProjectIdToRemove(projectId)
            toggleRemoveWarning()
        }
    }

    const handleRemoveProjectClick = () => {
        const removeProjectObj: RemoveProjectInput = {
            projectId: projectIdToRemove,
        }
        removeProject({
            variables: {
                removeProjectInput: removeProjectObj,
            },
        })
        toggleRemoveWarning()
    }

    const [removeProject] = useMutation<{
        removeProjectInput: RemoveProjectInput
    }>(RemoveProjectMutation, {
        onCompleted: () => {
            showGlobalSnackbar({
                variables: {
                    message: `Success! Project Removed`,
                    variant: 'success',
                    id: `ProjectHomePage_ProjectRemoveSuccess`,
                },
            })
            /*
             *   Added timeout to compensate for delay in updating of project groups on the API side
             */
            setTimeout(() => {
                setProjectIdToRemove('')
                triggerProjectFilter()
                setIsRecentProjectsRefresh(true)
            }, 1000)
        },
        onError: (error: ApolloError) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'ProjectHomePage_ProjectRemoveError',
                },
            })
        },
    })

    useEffect(() => {
        if (projectsError) {
            showGlobalSnackbar({
                variables: {
                    id: 'ProjectsHomePage_LoadingProjects',
                    variant: 'error',
                    message: 'Error loading projects.',
                },
            })
        } else if (firmProjectParametersError) {
            showGlobalSnackbar({
                variables: {
                    id: 'ProjectsHomePage_LoadingFirmProjectParameters',
                    variant: 'error',
                    message: 'Error loading firm project parameters',
                },
            })
        }
    }, [projectsError, showGlobalSnackbar, firmProjectParametersError])

    const handleViewChange = (
        _event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const isCardView = !toggleCardView
        setToggleCardView(isCardView)
        setProjectFilterValues(emptyFilterSelection)
        setSkipCount(0)
        setProjectSortValues([...initialSorting])
        if (userId) {
            updateKpMainLocalStore({
                currentUserId: userId,
                pageKey: localStorageKey,
                itemKey: localStorageItemKey(userId),
                itemValue: JSON.stringify({
                    filterValues: emptyFilterSelection,
                    filterView: isFilterExpanded,
                    isCardView: isCardView,
                    sortValues: [...initialSorting],
                }),
            })
        }
    }
    React.useEffect(() => {
        if (projectsData) {
            const initialExpandedVal: Record<string, boolean> = {}

            const uniqueProjectGroups = projectsData?.getProjects?.projects
                ?.map((item) => item.groupName)
                .filter((value, index, self) => self.indexOf(value) === index)
            uniqueProjectGroups.forEach((projectGroup, index) => {
                initialExpandedVal[
                    `groupName:${projectGroup}`
                ] = isGridExpanded[`groupName:${projectGroup}`]
                    ? isGridExpanded[`groupName:${projectGroup}`]
                    : index === 0 &&
                      isGridExpanded[`groupName:${projectGroup}`] === undefined
                    ? true
                    : false
            })
            setIsGridExpanded(initialExpandedVal)
        }
    }, [projectsData])

    const specTableTitleSettingsProps: SpecTableTitleSettingsProps<TableProjectItem> = {
        hasGrouping: true,
        onCardViewClick: handleViewChange,
        onFilterClick: handleFilterView,
        onTableViewClick: handleViewChange,
        showFiltersRow: isFilterExpanded,
        showTableSettings: false,
        tableViewActive: !toggleCardView,
        theme: 'light',
    }
    const specTableTitleSettingsGridView: SpecTableTitleSettingsProps<TableProjectItem> = {
        ...specTableTitleSettingsProps,
    }
    const specTableTitleSettingsTableView: SpecTableTitleSettingsProps<TableProjectItem> = {
        ...specTableTitleSettingsProps,
        tableSettings: {
            initialPinningProps: pinnedColumns,
        },
    }
    const tableTitle = `ALL PROJECTS (${projectsDisplayed} OF ${totalProjectsCount})`
    return (
        <>
            {!projectsLoading &&
            (!projectsData ||
                (projectsData.getProjects.count <= 0 &&
                    projectFilterValues.length === 0)) ? (
                <ProjectsPageEmptyState
                    onAddClick={handleAddProjectButtonClick}
                    variant={emptyStateVariant}
                    canCreateProject={canCreateProject}
                />
            ) : (
                <div className={classes.projectsHomePageContainer}>
                    <GridContainer
                        className={classes.gridContainer}
                        direction="column"
                        spacing={2}
                    >
                        <GridItem className={classes.projectsHeaderContainer}>
                            <GridItem className={classes.sectionHeader}>
                                Recent Projects
                            </GridItem>
                        </GridItem>
                        <GridItem className={classes.widgetContainer}>
                            <RecentProjectsWidget
                                handleProjectFavoriteStateChange={
                                    handleFavoriteButtonClick
                                }
                                reloadRecentProjects={isRecentProjectsRefresh}
                                triggerRecentProjectsReload={
                                    setIsRecentProjectsRefresh
                                }
                                openRemoveProjectConfirmation={openRemoveDialog}
                                isAdmin={isAdmin}
                                activeFirmInfo={activeFirmInfo}
                            />
                        </GridItem>
                        <GridItem className={classes.projectTitleBar}>
                            <ProjectTitleBar
                                projectTitle={tableTitle}
                                onAddClick={handleAddProjectButtonClick}
                                addProjectButton={showAddProjectButton}
                                canCreateProject={canCreateProject}
                            />
                        </GridItem>
                        <GridItem
                            className={
                                !toggleCardView
                                    ? classes.tableViewContainer
                                    : classes.projectViewContainer
                            }
                        >
                            {toggleCardView ? (
                                <>
                                    <div
                                        className={classes.gridViewSettingsBar}
                                    >
                                        <SpecTableTitleSettings
                                            {...specTableTitleSettingsGridView}
                                        ></SpecTableTitleSettings>
                                    </div>

                                    <ProjectFilterContext.Provider
                                        value={context}
                                    >
                                        <ProjectGridView
                                            skipCount={skipCount}
                                            setSkipCount={setSkipCount}
                                            handleExpandCollapse={
                                                handleExpandCollapse
                                            }
                                            isFilterExpanded={isFilterExpanded}
                                            isGridExpanded={isGridExpanded}
                                            projectData={
                                                projectsData?.getProjects
                                                    .projects
                                            }
                                            projectCount={
                                                projectsData?.getProjects.count
                                            }
                                            handleFavoriteChange={
                                                handleFavoriteButtonClick
                                            }
                                            projectDataStatus={
                                                projectDataStatus
                                            }
                                            projectsLoading={
                                                fetchNextLoading ||
                                                projectsLoading
                                            }
                                            fetchNextData={fetchNextData}
                                            defaultPageSize={defaultPageSize}
                                            localStorageKey={localStorageKey}
                                            localStorageItemKey={
                                                localStorageItemKey
                                            }
                                            projectGroupCounts={
                                                projectsData?.getProjects
                                                    .projectGroupCounts
                                            }
                                            projectSortValues={
                                                projectSortValues
                                            }
                                            openRemoveProjectConfirmation={
                                                openRemoveDialog
                                            }
                                            isAdmin={isAdmin}
                                            activeFirmInfo={activeFirmInfo}
                                        />
                                    </ProjectFilterContext.Provider>
                                </>
                            ) : (
                                <ProjectFilterContext.Provider value={context}>
                                    <ProjectTableView
                                        specTableTitleSettings={
                                            specTableTitleSettingsTableView
                                        }
                                        projectData={
                                            projectsData?.getProjects.projects
                                        }
                                        handleFavoriteChange={
                                            handleFavoriteButtonClick
                                        }
                                        projectsLoading={fetchNextLoading}
                                        fetchNextData={fetchNextData}
                                        projectDataStatus={projectDataStatus}
                                        defaultPageSize={defaultPageSize}
                                        buildingTypeDropDown={
                                            buildingTypeDropDown
                                        }
                                        projectPhaseDropDown={
                                            projectPhaseDropDown
                                        }
                                        lodDropDown={lodDropDown}
                                        setSkipCount={setSkipCount}
                                        localStorageKey={localStorageKey}
                                        localStorageItemKey={
                                            localStorageItemKey
                                        }
                                        setProjectSortValues={
                                            setProjectSortValues
                                        }
                                        projectCount={
                                            projectsData?.getProjects.count ?? 0
                                        }
                                        projectGroupCounts={
                                            projectsData?.getProjects
                                                .projectGroupCounts
                                        }
                                        budgetDropDown={budgetDropDown}
                                        regionDropDown={regionDropDown}
                                        countryDropDown={countryDropDown}
                                        projectSortValues={projectSortValues}
                                        expanded={expanded}
                                        setExpanded={setExpanded}
                                        triggerProjectListReload={
                                            triggerProjectFilter
                                        }
                                        isProjectHome={true}
                                        triggerRecentProjectsReload={
                                            setIsRecentProjectsRefresh
                                        }
                                        openRemoveProjectConfirmation={
                                            openRemoveDialog
                                        }
                                    />
                                </ProjectFilterContext.Provider>
                            )}
                        </GridItem>
                    </GridContainer>
                </div>
            )}
            <NewProjectSlideOut
                open={slideOutOpen}
                onClose={handleCloseSlideOut}
                triggerProjectListReload={triggerProjectFilter}
                setSkipCount={setSkipCount}
            />
            {isOpenConfirmation && (
                <RemoveProjectWarning
                    isDisplayed={isOpenConfirmation}
                    onCancel={toggleRemoveWarning}
                    onRemoveProject={handleRemoveProjectClick}
                />
            )}
        </>
    )
}

export default ProjectsHomePage
